import React, { useState, useEffect } from "react";
import axios from "axios";
import { Listbox } from "@headlessui/react";
import { ChevronsUpDownIcon } from "lucide-react";

function ThemedDropdown({ label, options, selected, setSelected, searchable }) {
  const [search, setSearch] = useState("");

  // Search matches displayName, userId, or name
  const filteredOptions = options.filter(opt =>
    (opt.displayName || opt.name).toLowerCase().includes(search.toLowerCase()) ||
    (opt.userId && opt.userId.toString().includes(search))
  );

  return (
    <div className="relative w-full sm:w-[250px] text-[10px] md:text-[12px]">
      <Listbox value={selected} onChange={setSelected}>
        <div className="relative">
          <Listbox.Button className="w-full cursor-pointer rounded-lg border-2 border-gray-300 bg-white px-3 py-2 text-left shadow-sm focus:outline-none focus:ring-0 focus:ring-green-500 focus:border-green-500 text-gray-500">
            {selected ? (selected.displayName || selected.name) : label}
            <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <ChevronsUpDownIcon className="h-5 w-5 text-gray-500" />
            </span>
          </Listbox.Button>
          <Listbox.Options className="absolute text-gray-500 left-0 right-0 z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-[12px]">
            {searchable && (
              <div className="px-3 py-2 sticky top-0 bg-white z-10">
                <input
                  type="text"
                  className="w-full border border-green-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>
            )}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-gray-500">No results</div>
            )}
            {filteredOptions.map(option => (
              <Listbox.Option
                key={option.id}
                value={option}
                className={({ active }) =>
                  `cursor-pointer select-none px-3 py-2 ${
                    active ? "bg-green-100 text-green-900" : "text-gray-500"
                  }`
                }
              >
                {option.displayName || option.name}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
}

export default function AssignPlanB2b() {
  const [usersList, setUsersList] = useState([]);
  const [assignedPlansData, setAssignedPlansData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Fixed list of plans
  const plansList = [
    { id: "bronze", name: "Bronze - 45%" },
    { id: "silver", name: "Silver - 35%" },
    { id: "gold", name: "Gold - 25%" },
    { id: "platinum", name: "Platinum - 15%" },
  ];

  // Fetch all users from backend
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/assignPlan/get-users`
        );
        setUsersList(
          (res.data.users || []).map(u => ({
            id: u.id,
            userId: u.userId,
            name: u.name,
            displayName: `${u.userId} - ${u.name}`,
          }))
        );
      } catch (err) {
        setUsersList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch assigned plans for table display
  const fetchAssignedPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/assignPlan/assigned-plans`
      );
      setAssignedPlansData(res.data.plans || []);
    } catch (err) {
      setAssignedPlansData([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAssignedPlans();
  }, []);

  // Handler to save the assignment
  const handleSave = async () => {
    if (!selectedUser || !selectedPlan) {
      alert("Please select both a user and a plan.");
      return;
    }
    try {
      setLoading(true);
      const body = {
        userId: selectedUser.userId, // numeric/string
        userName: selectedUser.name, // full name only
        planName: selectedPlan.name,
      };
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/assignPlan/assign-plan`,
        body
      );
      alert("Plan assigned!");
      setSelectedUser(null);
      setSelectedPlan(null);
      fetchAssignedPlans();
    } catch (err) {
      alert("Failed to assign plan.");
    } finally {
      setLoading(false);
    }
  };

  // Filter assignments by userName or planName
  const filteredData = assignedPlansData.filter(
    item =>
      item.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.planName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto sm:px-2 p-1">
      {/* Top row: Dropdowns and Save */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <ThemedDropdown
          label="Select User"
          options={usersList}
          selected={selectedUser}
          setSelected={setSelectedUser}
          searchable
        />
        <ThemedDropdown
          label="Select Plan"
          options={plansList}
          selected={selectedPlan}
          setSelected={setSelectedPlan}
          searchable={false}
        />
        <div className="w-full md:w-auto">
          <button
            type="button"
            className="w-full md:w-auto border-2 bg-[#0CBB7D] text-white font-[600] py-2 px-3 rounded-lg shadow-sm hover:bg-[#0CBB7D] focus:outline-none focus:ring-2 focus:ring-[#0CBB7D] focus:ring-opacity-50 text-[10px] md:text-[12px]"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="my-2">
        <input
          type="text"
          placeholder="Search by User or Plan Name"
          className="block w-full sm:w-[300px] px-3 py-2 border-2 border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:ring-[#0CBB7D] focus:border-[#0CBB7D] text-[10px] md:text-[12px]"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          autoComplete="off"
        />
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 bg-white shadow-sm text-[12px]">
          <thead>
            <tr className="bg-[#0CBB7D] text-white border border-[#0CBB7D]">
              <th className="px-3 py-2 text-left uppercase">SL. No.</th>
              <th className="px-3 py-2 text-left uppercase">User</th>
              <th className="px-3 py-2 text-left uppercase">Assigned Plan</th>
              <th className="px-3 py-2 text-left uppercase">Assigned Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-6 text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((item, idx) => (
                <tr
                  key={item.id}
                  className="text-gray-500 hover:bg-gray-50 text-[12px] border border-gray-300"
                >
                  <td className="px-3 py-2">{item.slNo || idx + 1}</td>
                  <td className="px-3 py-2">{item.userName}</td>
                  <td className="px-3 py-2">{item.planName}</td>
                  <td className="px-3 py-2">
                    {new Date(item.assignedAt).toLocaleString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center py-6 text-gray-500">
                  No assignments found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-2">
        {filteredData.length > 0 ? (
          filteredData.map(item => (
            <div
              key={item.id}
              className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
            >
              <div className="text-[12px] font-[600] text-gray-500 mb-1">
                User:{" "}
                <span className="font-normal text-gray-500">
                  {item.userName}
                </span>
              </div>
              <div className="text-[12px] font-[600] text-gray-500 mb-1">
                Assigned Plan:{" "}
                <span className="font-normal text-gray-500">
                  {item.planName}
                </span>
              </div>
              <div className="text-[12px] font-[600] text-gray-500">
                Assigned Date:{" "}
                <span className="font-normal text-gray-500">
                  {new Date(item.assignedAt).toLocaleString()}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6 text-gray-500">
            No assignments found.
          </div>
        )}
      </div>
    </div>
  );
}
